import { QueryKeys } from "@/constants/Query";
import { usePatientDemographics } from "@/features/Patient/PatientBar/network/usePatientDemographics";
import {
  type DuplicatePatient,
  useDuplicatePatients,
} from "@/network/useDuplicatePatients";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useRef, useState } from "react";

export const useDuplicateRecordAlert = (patientId: string) => {
  const queryClient = useQueryClient();
  const { data: demographics } = usePatientDemographics(patientId);
  const [isDismissed, setIsDismissed] = useState(false);
  const prevPatientIdRef = useRef(patientId);

  if (prevPatientIdRef.current !== patientId) {
    prevPatientIdRef.current = patientId;
    setIsDismissed(false);
  }

  const { data: duplicateData } = useDuplicatePatients({
    patientId,
    firstName: demographics?.demographics.nameFirst,
    lastName: demographics?.demographics.nameLast,
    dateOfBirth: demographics?.demographics.dob,
  });

  const duplicatePatients: DuplicatePatient[] = duplicateData?.patients ?? [];
  const isDuplicateDialogOpen = duplicatePatients.length > 0 && !isDismissed;

  const dismissDuplicateDialog = useCallback(() => {
    setIsDismissed(true);
    queryClient.removeQueries({
      queryKey: QueryKeys.Patient.Duplicates.DEFAULT(patientId),
    });
  }, [patientId, queryClient]);

  const suppressDuplicateDialogForPatient = useCallback(
    (targetPatientId: string) => {
      queryClient.setQueryData(
        QueryKeys.Patient.Duplicates.DEFAULT(targetPatientId),
        { patients: [] },
      );
    },
    [queryClient],
  );

  return {
    isDuplicateDialogOpen,
    duplicatePatients,
    dismissDuplicateDialog,
    suppressDuplicateDialogForPatient,
  };
};
